import { useEffect, useRef, useState, FormEvent } from 'react'

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:3001'

const LOGO_MIME = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']

const DOC_TYPES = [
  { value: 'KBIS',      label: 'Kbis / Extrait Kbis',             icon: '🏢' },
  { value: 'ASSURANCE', label: 'Attestation d\'assurance RC',      icon: '🛡️' },
  { value: 'RIB',       label: 'RIB / Coordonnées bancaires',      icon: '🏦' },
  { value: 'CONTRAT',   label: 'Modèle de contrat prestataire',    icon: '📋' },
  { value: 'AUTRE',     label: 'Autre document',                   icon: '📎' },
]

interface CompanyDoc { id: string; type: string; name: string; filename: string; size: number; createdAt: string }

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} o`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`
}

function authHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem('velink_token')}`,
    'Content-Type': 'application/json',
  }
}

interface CompanyProfile {
  id: string; name: string; email: string; siret: string | null; phone: string | null; isCnpas: boolean; logoUrl: string | null
}

export default function ParametresPage() {
  const [profile, setProfile] = useState<CompanyProfile | null>(null)
  const [profileMsg, setProfileMsg] = useState('')
  const [pwdMsg, setPwdMsg] = useState('')
  const [emailMsg, setEmailMsg] = useState('')
  const [loading, setLoading] = useState(false)
  const [pwdLoading, setPwdLoading] = useState(false)
  const [emailLoading, setEmailLoading] = useState(false)
  const [cnpasEnabled, setCnpasEnabled] = useState(false)
  const [cnpasMsg, setCnpasMsg] = useState('')
  const [cnpasLoading, setCnpasLoading] = useState(false)
  const [docs, setDocs] = useState<CompanyDoc[]>([])
  const [docsLoading, setDocsLoading] = useState(true)
  const [docsError, setDocsError] = useState('')
  const [docsSuccess, setDocsSuccess] = useState('')
  const [uploading, setUploading] = useState(false)
  const [selectedDocType, setSelectedDocType] = useState('KBIS')
  const fileRef = useRef<HTMLInputElement>(null)
  const logoRef = useRef<HTMLInputElement>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [logoUploading, setLogoUploading] = useState(false)
  const [logoMsg, setLogoMsg] = useState('')

  useEffect(() => {
    fetch(`${API}/api/auth/me`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((data: CompanyProfile) => {
        setProfile(data)
        setCnpasEnabled(data.isCnpas ?? false)
        if (data.logoUrl) {
          const [companyId, filename] = data.logoUrl.split('/')
          fetch(`${API}/api/companies/logo/${companyId}/${filename}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('velink_token')}` },
          })
            .then((r) => r.ok ? r.blob() : null)
            .then((blob) => blob ? setLogoPreview(URL.createObjectURL(blob)) : null)
            .catch(() => {})
        }
      })

    fetch(`${API}/api/companies/me/documents`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('velink_token')}` },
    })
      .then((r) => r.ok ? r.json() : [])
      .then(setDocs)
      .catch(() => {})
      .finally(() => setDocsLoading(false))
  }, [])

  async function uploadLogo(file: File) {
    if (!LOGO_MIME.includes(file.type)) { setLogoMsg('Format non autorisé (JPG, PNG, WebP uniquement)'); return }
    setLogoUploading(true)
    setLogoMsg('')
    const preview = URL.createObjectURL(file)
    setLogoPreview(preview)
    const fd = new FormData()
    fd.append('file', file)
    try {
      const res = await fetch(`${API}/api/companies/me/logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('velink_token')}` },
        body: fd,
      })
      if (!res.ok) throw new Error('Erreur lors de l\'upload')
      const data = await res.json()
      setProfile((p) => p ? { ...p, logoUrl: data.logoUrl } : p)
      setLogoMsg('Logo mis à jour ✓')
    } catch (e: unknown) {
      setLogoMsg(e instanceof Error ? e.message : 'Erreur')
      setLogoPreview(null)
    } finally {
      setLogoUploading(false)
    }
  }

  async function uploadDoc(file: File) {
    if (uploading) return
    setDocsError('')
    setDocsSuccess('')
    setUploading(true)
    const fd = new FormData()
    fd.append('type', selectedDocType)
    fd.append('file', file)
    try {
      const res = await fetch(`${API}/api/companies/me/documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('velink_token')}` },
        body: fd,
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message ?? 'Erreur upload')
      setDocs((prev) => [data, ...prev])
      setDocsSuccess('Document ajouté ✓')
      if (fileRef.current) fileRef.current.value = ''
    } catch (e: unknown) {
      setDocsError(e instanceof Error ? e.message : 'Erreur lors de l\'upload')
    } finally {
      setUploading(false)
    }
  }

  async function deleteDoc(id: string) {
    await fetch(`${API}/api/companies/me/documents/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${localStorage.getItem('velink_token')}` },
    })
    setDocs((prev) => prev.filter((d) => d.id !== id))
  }

  async function onSaveProfile(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setLoading(true)
    setProfileMsg('')
    const fd = new FormData(e.currentTarget)
    try {
      const res = await fetch(`${API}/api/auth/profile`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({
          name: fd.get('name'),
          phone: fd.get('phone'),
          siret: fd.get('siret'),
        }),
      })
      if (!res.ok) throw new Error('Erreur lors de la mise à jour')
      const updated = await res.json()
      setProfile((p) => p ? { ...p, ...updated } : p)
      setProfileMsg('Profil mis à jour ✓')
    } catch (err: unknown) {
      setProfileMsg(err instanceof Error ? err.message : 'Erreur')
    } finally {
      setLoading(false)
    }
  }

  async function onChangePassword(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setPwdLoading(true)
    setPwdMsg('')
    const fd = new FormData(e.currentTarget)
    const newPwd = fd.get('newPassword') as string
    const confirm = fd.get('confirmPassword') as string
    if (newPwd !== confirm) { setPwdMsg('Les mots de passe ne correspondent pas'); setPwdLoading(false); return }
    try {
      const res = await fetch(`${API}/api/auth/password`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ currentPassword: fd.get('currentPassword'), newPassword: newPwd }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message ?? 'Erreur')
      setPwdMsg('Mot de passe mis à jour ✓')
      ;(e.target as HTMLFormElement).reset()
    } catch (err: unknown) {
      setPwdMsg(err instanceof Error ? err.message : 'Erreur')
    } finally {
      setPwdLoading(false)
    }
  }

  async function onChangeEmail(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setEmailLoading(true)
    setEmailMsg('')
    const fd = new FormData(e.currentTarget)
    try {
      const res = await fetch(`${API}/api/auth/email`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ newEmail: fd.get('newEmail'), currentPassword: fd.get('currentPassword') }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message ?? 'Erreur')
      localStorage.setItem('velink_token', data.accessToken)
      setProfile((prev) => prev ? { ...prev, email: fd.get('newEmail') as string } : prev)
      setEmailMsg('Adresse email mise à jour ✓')
      ;(e.target as HTMLFormElement).reset()
    } catch (err: unknown) {
      setEmailMsg(err instanceof Error ? err.message : 'Erreur')
    } finally {
      setEmailLoading(false)
    }
  }

  if (!profile) return <div className="text-text-muted animate-pulse">Chargement…</div>

  return (
    <div className="max-w-2xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-text-primary">Mon compte</h1>
        <p className="text-text-secondary mt-1">{profile.email}</p>
      </div>

      {/* Logo */}
      <div className="glass-card p-6 mb-6">
        <h2 className="text-lg font-semibold text-text-primary mb-5">Logo de l'entreprise</h2>
        <div className="flex items-center gap-5">
          <div
            onClick={() => logoRef.current?.click()}
            className="relative w-20 h-20 rounded-2xl overflow-hidden cursor-pointer group flex-shrink-0 border border-white/10 hover:border-accent/40 transition-colors"
          >
            {logoPreview ? (
              <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-white/[0.06] flex items-center justify-center text-2xl font-bold text-text-muted">
                {profile?.name?.charAt(0).toUpperCase() ?? '?'}
              </div>
            )}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
            </div>
            {logoUploading && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              </div>
            )}
          </div>
          <input
            ref={logoRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(f) }}
          />
          <div>
            <p className="text-text-secondary text-sm">Cliquez sur le logo pour le modifier.</p>
            <p className="text-text-muted text-xs mt-1">JPG, PNG, WebP — carré recommandé, max 5 Mo</p>
            {logoMsg && (
              <p className={`text-xs mt-2 ${logoMsg.includes('✓') ? 'text-success' : 'text-danger'}`}>{logoMsg}</p>
            )}
          </div>
        </div>
      </div>

      {/* Profil */}
      <div className="glass-card p-6 mb-6">
        <h2 className="text-lg font-semibold text-text-primary mb-5">Informations de l'entreprise</h2>
        <form onSubmit={onSaveProfile} className="space-y-4">
          <div>
            <label className="glass-label">Nom de l'entreprise</label>
            <input name="name" defaultValue={profile.name ?? ''} className="glass-input" required />
          </div>
          <div>
            <label className="glass-label">Téléphone</label>
            <input name="phone" defaultValue={profile.phone ?? ''} className="glass-input" placeholder="+33 6 00 00 00 00" />
          </div>
          <div>
            <label className="glass-label">SIRET</label>
            <input name="siret" defaultValue={profile.siret ?? ''} className="glass-input" placeholder="12345678901234" />
          </div>
          {profileMsg && (
            <p className={profileMsg.includes('✓') ? 'text-success text-sm' : 'text-danger text-sm'}>{profileMsg}</p>
          )}
          <button type="submit" disabled={loading} className="glass-btn-accent px-6 py-2.5 text-sm rounded-xl">
            {loading ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </form>
      </div>

      {/* Documents entreprise */}
      <div className="glass-card p-6 mb-6">
        <h2 className="text-lg font-semibold text-text-primary mb-1">Documents de l'entreprise</h2>
        <p className="text-text-secondary text-sm mb-5">Kbis, assurance, RIB, contrats — stockés de façon sécurisée.</p>

        {/* Type selector */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
          {DOC_TYPES.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setSelectedDocType(t.value)}
              className={[
                'flex items-center gap-2 px-3 py-2 rounded-xl text-sm border transition-all text-left',
                selectedDocType === t.value
                  ? 'bg-accent/15 border-accent/40 text-accent'
                  : 'border-white/10 text-text-secondary hover:border-white/20',
              ].join(' ')}
            >
              <span>{t.icon}</span>
              <span className="leading-tight text-xs">{t.label}</span>
            </button>
          ))}
        </div>

        {/* Drop zone */}
        <div
          onClick={() => fileRef.current?.click()}
          className="border-2 border-dashed border-white/15 rounded-xl p-6 text-center cursor-pointer hover:border-white/30 hover:bg-white/[0.02] transition-all"
        >
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadDoc(f) }}
          />
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <div className="w-6 h-6 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
              <p className="text-text-secondary text-sm">Upload en cours…</p>
            </div>
          ) : (
            <>
              <p className="text-text-primary text-sm font-medium">
                📤 Glissez un fichier ou <span className="text-accent">parcourir</span>
              </p>
              <p className="text-text-muted text-xs mt-1">PDF, JPG, PNG — max 10 Mo</p>
            </>
          )}
        </div>

        {docsError && <p className="mt-2 text-danger text-sm">{docsError}</p>}
        {docsSuccess && <p className="mt-2 text-success text-sm">{docsSuccess}</p>}

        {/* Liste */}
        {!docsLoading && docs.length > 0 && (
          <div className="mt-5 space-y-2">
            <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">
              Mes documents ({docs.length})
            </p>
            {docs.map((doc) => {
              const typeInfo = DOC_TYPES.find((t) => t.value === doc.type)
              return (
                <div
                  key={doc.id}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.07] group hover:bg-white/[0.07] transition-colors"
                >
                  <span className="text-lg flex-shrink-0">{typeInfo?.icon ?? '📎'}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-text-primary text-sm font-medium truncate">{doc.name}</p>
                    <p className="text-text-muted text-xs mt-0.5">
                      {typeInfo?.label ?? doc.type} · {formatSize(doc.size)} · {new Date(doc.createdAt).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={async () => {
                        const [companyId, filename] = doc.filename.split('/')
                        const url = `${API}/api/companies/me/documents/file/${companyId}/${filename}`
                        try {
                          const res = await fetch(url, { headers: authHeaders() })
                          if (!res.ok) throw new Error()
                          const blob = await res.blob()
                          const objectUrl = URL.createObjectURL(blob)
                          window.open(objectUrl, '_blank')
                          setTimeout(() => URL.revokeObjectURL(objectUrl), 60000)
                        } catch { window.open(url, '_blank') }
                      }}
                      title="Voir"
                      className="p-1.5 rounded-lg hover:bg-white/[0.08] text-text-secondary hover:text-text-primary transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/>
                      </svg>
                    </button>
                    <button
                      onClick={() => deleteDoc(doc.id)}
                      title="Supprimer"
                      className="p-1.5 rounded-lg hover:bg-danger/10 text-text-secondary hover:text-danger transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                      </svg>
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Email */}
      <div className="glass-card p-6">
        <h2 className="text-lg font-semibold text-text-primary mb-1">Adresse email</h2>
        {profile?.email && (
          <p className="text-text-muted text-xs mb-5">Actuelle : <span className="text-text-secondary">{profile.email}</span></p>
        )}
        <form onSubmit={onChangeEmail} className="space-y-4">
          <div>
            <label className="glass-label">Nouvelle adresse email</label>
            <input type="email" name="newEmail" className="glass-input" required placeholder="nouvelle@email.com" />
          </div>
          <div>
            <label className="glass-label">Mot de passe actuel (confirmation)</label>
            <input type="password" name="currentPassword" className="glass-input" required />
          </div>
          {emailMsg && (
            <p className={emailMsg.includes('✓') ? 'text-success text-sm' : 'text-danger text-sm'}>{emailMsg}</p>
          )}
          <button type="submit" disabled={emailLoading} className="glass-btn px-6 py-2.5 text-sm rounded-xl">
            {emailLoading ? 'Mise à jour…' : 'Changer l\'adresse email'}
          </button>
        </form>
      </div>

      {/* CNPAS */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
              <span>🛡️</span> Référencement CNPAS
            </h2>
            <p className="text-text-muted text-xs mt-0.5">Centre National de Prévention et de Protection — sécurité incendie et surveillance</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${cnpasEnabled ? 'bg-success/10 border-success/25 text-success' : 'bg-white/[0.06] border-white/[0.10] text-text-muted'}`}>
            {cnpasEnabled ? '✓ CNPAS activé' : 'Non référencé'}
          </span>
        </div>
        <p className="text-text-secondary text-sm mb-4">
          En activant le référencement CNPAS, vous pouvez rechercher des prestataires <strong>ADS</strong> (Agent de Défense et Sécurité) et <strong>SSIAP</strong> (Sécurité Incendie et Assistance à Personnes), et recevoir des demandes de missions liées à la sécurité.
        </p>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <div
              onClick={() => setCnpasEnabled((v) => !v)}
              className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${cnpasEnabled ? 'bg-accent' : 'bg-white/[0.12]'}`}
            >
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${cnpasEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </div>
            <span className="text-sm text-text-secondary">{cnpasEnabled ? 'CNPAS activé' : 'CNPAS désactivé'}</span>
          </label>
          <button
            onClick={async () => {
              setCnpasMsg('')
              setCnpasLoading(true)
              try {
                const res = await fetch(`${API}/api/auth/profile`, {
                  method: 'PATCH',
                  headers: authHeaders(),
                  body: JSON.stringify({ isCnpas: cnpasEnabled }),
                })
                if (!res.ok) throw new Error()
                setProfile((prev) => prev ? { ...prev, isCnpas: cnpasEnabled } : prev)
                setCnpasMsg('Statut CNPAS mis à jour.')
                setTimeout(() => setCnpasMsg(''), 3000)
              } catch {
                setCnpasMsg('Erreur lors de la mise à jour.')
              } finally {
                setCnpasLoading(false)
              }
            }}
            disabled={cnpasLoading}
            className="glass-btn px-5 py-2 text-sm rounded-xl"
          >
            {cnpasLoading ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
        {cnpasMsg && <p className={`text-sm mt-3 ${cnpasMsg.includes('Erreur') ? 'text-danger' : 'text-success'}`}>{cnpasMsg}</p>}
      </div>

      {/* Mot de passe */}
      <div className="glass-card p-6">
        <h2 className="text-lg font-semibold text-text-primary mb-5">Changer le mot de passe</h2>
        <form onSubmit={onChangePassword} className="space-y-4">
          <div>
            <label className="glass-label">Mot de passe actuel</label>
            <input type="password" name="currentPassword" className="glass-input" required />
          </div>
          <div>
            <label className="glass-label">Nouveau mot de passe</label>
            <input type="password" name="newPassword" className="glass-input" minLength={8} required />
          </div>
          <div>
            <label className="glass-label">Confirmer le nouveau mot de passe</label>
            <input type="password" name="confirmPassword" className="glass-input" required />
          </div>
          {pwdMsg && (
            <p className={pwdMsg.includes('✓') ? 'text-success text-sm' : 'text-danger text-sm'}>{pwdMsg}</p>
          )}
          <button type="submit" disabled={pwdLoading} className="glass-btn px-6 py-2.5 text-sm rounded-xl">
            {pwdLoading ? 'Mise à jour…' : 'Changer le mot de passe'}
          </button>
        </form>
      </div>
    </div>
  )
}
